import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useListContacts } from "@workspace/api-client-react";
import type { ContactDirectoryRow } from "@workspace/api-client-react";
import { memId } from "@/lib/display-id";
import { cn } from "@/lib/utils";

// Picker that lists EVERY contact across all accounts, with the parent
// account's legalName + phone joined inline. Drives the Pay-Per-Use
// stock-out consumer picker — the operator identifies the actual
// person who consumed the item, not just the billable company.
//
// Each row:
//   line 1 — contact fullName  (primary)  MEM-XXXX badge
//   line 2 — account legalName · phone     (secondary, single line)

interface ContactComboboxProps {
  // The Account ID of the selected contact. We submit Account.id because
  // stock_out.member_id currently points at accounts (post-23-08). A
  // future `contact_id` column would let us record the specific person;
  // for now the picker is contact-level for display, but submit is the
  // contact's parent account.
  value: number | null;
  onChange: (accountId: number, contact: ContactDirectoryRow) => void;
  className?: string;
  autoFocus?: boolean;
}

export function ContactCombobox({ value, onChange, className, autoFocus }: ContactComboboxProps) {
  const [open, setOpen] = useState(false);
  const { data: contacts = [] } = useListContacts();

  // Multiple contacts can share an accountId, so `value` alone doesn't
  // uniquely identify a row. Match by accountId AND prefer primary, so
  // the trigger label is stable when the operator picks a non-primary
  // contact and re-opens the popover.
  const selected =
    contacts.find((c) => c.accountId === value && c.roles?.includes("primary")) ??
    contacts.find((c) => c.accountId === value) ??
    null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          autoFocus={autoFocus}
          className={cn("w-full justify-between h-12 text-base", className)}
        >
          {selected ? (
            <span className="flex flex-col items-start min-w-0">
              <span className="flex items-center gap-2 text-sm">
                <span className="font-medium truncate">{selected.fullName}</span>
                <span className="font-mono text-xs text-muted-foreground shrink-0">
                  {memId(selected.accountId)}
                </span>
              </span>
              <span className="text-xs text-muted-foreground truncate">
                {selected.accountLegalName} · {selected.phone}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">Search by name, company, or phone…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search by name, company, or phone…"
            aria-label="Search contacts"
          />
          <CommandList>
            <CommandEmpty>No contact matches your query.</CommandEmpty>
            <CommandGroup>
              {contacts.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.fullName} ${c.accountLegalName} ${memId(c.accountId)} ${c.phone} ${c.email ?? ""}`}
                  onSelect={() => {
                    onChange(c.accountId, c);
                    setOpen(false);
                  }}
                  className="py-2"
                >
                  <div className="flex w-full flex-col gap-0.5">
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">{c.fullName}</span>
                      <span className="font-mono text-xs text-muted-foreground shrink-0">
                        {memId(c.accountId)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {c.accountLegalName} · {c.phone}
                    </div>
                  </div>
                  {selected?.id === c.id ? <Check className="ml-2 h-4 w-4" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
